import { useIsMutating } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, Check, Cloud, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { queryClient } from "@/lib/trpc";
import { useSyncStore, type SyncStatus } from "@/stores/sync.store";

/**
 * EMS ERP Top Header Smart Sync Indicator
 * - Isolated Zustand & TanStack Query subscriber (0 parent Header re-renders)
 * - Desktop (>=768px): Expands into a smooth emerald pill `[⟳ Đang lưu...]` -> `[✓ Đã lưu]`, then collapses to a clean icon
 * - Mobile (<768px): Fixed 32x32px circle turning vivid emerald on success (zero horizontal overflow)
 * - Accessibility: Polite live region announces settled SUCCESS only (no interstitial loading chatter)
 */
export function HeaderSyncIndicator() {
  const isMutating = useIsMutating();
  const storeSyncing = useSyncStore((state) => state.isSyncing);
  const syncStatus = useSyncStore((state) => state.status);
  const shortLabel = useSyncStore((state) => state.shortLabel);
  const syncMessage = useSyncStore((state) => state.message);
  const syncAction = useSyncStore((state) => state.action);
  const lastError = useSyncStore((state) => state.lastError);
  const lastWarning = useSyncStore((state) => state.lastWarning);
  const lastSyncedAt = useSyncStore((state) => state.lastSyncedAt);

  const isSyncing = storeSyncing || isMutating > 0;

  let currentStatus: SyncStatus = "IDLE";
  if (isSyncing) {
    currentStatus = "SYNCING";
  } else if (syncStatus === "ERROR") {
    currentStatus = "ERROR";
  } else if (syncStatus === "WARNING") {
    currentStatus = "WARNING";
  } else if (syncStatus === "SUCCESS") {
    currentStatus = "SUCCESS";
  }

  const handleClick = async () => {
    if (currentStatus === "SUCCESS" && syncAction) {
      const callback = syncAction.onClick;
      useSyncStore.getState().resetStatus();
      callback();
      return;
    }

    if (isSyncing) {
      return;
    }

    useSyncStore.getState().beginTask("Đang đồng bộ dữ liệu với máy chủ...");
    try {
      await queryClient.invalidateQueries();
      useSyncStore.getState().completeTask(true, "Đã đồng bộ dữ liệu đám mây mới nhất");
    } catch {
      useSyncStore.getState().completeTask(false);
      useSyncStore.getState().setError("Không thể đồng bộ với máy chủ");
    }
  };

  const tooltipText =
    currentStatus === "SYNCING"
      ? syncMessage || "Đang lưu và đồng bộ dữ liệu..."
      : currentStatus === "SUCCESS"
        ? syncAction
          ? `${syncMessage || "Đã lưu thành công"} • Bấm để ${syncAction.label.toLowerCase()}`
          : syncMessage || "Đã lưu và đồng bộ thành công"
        : currentStatus === "WARNING"
          ? `Cảnh báo: ${lastWarning || syncMessage || "Có cảnh báo nghiệp vụ"}`
          : currentStatus === "ERROR"
            ? `Lỗi: ${lastError || syncMessage || "Đã có lỗi xảy ra khi lưu dữ liệu"}`
            : `Hệ thống đã đồng bộ${
                lastSyncedAt
                  ? ` lúc ${new Date(lastSyncedAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`
                  : ""
              } • Bấm để làm mới dữ liệu`;

  const isExpandedPill = currentStatus === "SYNCING" || currentStatus === "SUCCESS";

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={handleClick}
        title={tooltipText}
        aria-label={tooltipText}
        data-testid="header-sync-indicator"
        data-sync-status={currentStatus}
        data-sync-message={syncMessage}
        className={cn(
          "relative h-8 rounded-full transition-all duration-200 shrink-0 cursor-pointer",
          isExpandedPill ? "w-8 p-0 md:w-auto md:px-2.5 md:gap-1.5" : "w-8 p-0",
          currentStatus === "SYNCING" &&
            "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100",
          currentStatus === "SUCCESS" &&
            "bg-emerald-500 text-white hover:bg-emerald-600 shadow-xs ring-2 ring-emerald-500/25",
          currentStatus === "WARNING" &&
            "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100",
          currentStatus === "ERROR" &&
            "bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100",
          currentStatus === "IDLE" &&
            "text-slate-500 hover:text-emerald-600 hover:bg-emerald-50",
        )}
      >
        {currentStatus === "SYNCING" ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600 shrink-0" />
            <span className="hidden md:inline text-xs font-semibold whitespace-nowrap">
              Đang lưu...
            </span>
          </>
        ) : currentStatus === "SUCCESS" ? (
          <>
            <Check className="h-3.5 w-3.5 stroke-[2.75] text-white shrink-0 animate-in zoom-in-75 duration-150" />
            <span className="hidden md:inline text-xs font-semibold max-w-[160px] truncate">
              {shortLabel || "Đã lưu"}
            </span>
            {syncAction && (
              <span className="hidden md:inline text-xs font-bold underline underline-offset-2 whitespace-nowrap">
                · {syncAction.label}
              </span>
            )}
          </>
        ) : currentStatus === "WARNING" ? (
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
        ) : currentStatus === "ERROR" ? (
          <AlertCircle className="h-4 w-4 text-rose-600 shrink-0" />
        ) : (
          <Cloud className="h-4 w-4 text-slate-400 hover:text-emerald-600 transition-colors shrink-0" />
        )}

        {currentStatus === "WARNING" && (
          <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
        )}
        {currentStatus === "ERROR" && (
          <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />
        )}
      </Button>

      <span role="status" aria-live="polite" className="sr-only">
        {currentStatus === "SUCCESS"
          ? syncMessage || shortLabel || "Đã lưu thành công"
          : ""}
      </span>
    </>
  );
}
